import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowCounterClockwise,
  ArrowSquareOut,
  Calendar,
  Copy,
  DotsThree,
  DownloadSimple,
  Eye,
  EyeSlash,
  Globe,
  PencilSimple,
  Plus,
  Trash,
  XCircle,
} from '@phosphor-icons/react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  listWebsites,
  publishWebsite,
  unpublishWebsite,
  deleteWebsite,
  duplicateWebsite,
  discardWebsitePending,
  schedulePublishWebsite,
  cancelScheduledPublishWebsite,
  type Website,
} from '../api/websites';
import { config } from '../config';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { useConfirm } from '../components/ConfirmDialog';
import { ScheduleDateTimeDialog } from '../components/ScheduleDateTimeDialog';
import { WebsiteImportWizard } from '../components/WebsiteImportWizard';
import { Button } from '@/components/ui/button';
import {
  RowActions,
  StickyEndCell,
  useHorizontalScrollEdge,
  type RowAction,
} from '@/components/ui/row-actions';
import {
  SortableHeaderButton,
  useTableSortState,
} from '@/components/ui/table-sort';
import { cn } from '@/lib/utils';

// WebsitePage is the list view at /orgs/:slug/website. Each row is one
// website variant; each carries its own public slug under /c/<slug> so
// multiple sites per org (different sports, different age groups) can
// be live in parallel. The Add button kicks off the wizard at
// /:slug/website/new; subsequent edits jump straight to the design page
// at /:slug/website/<id>/design.

type SortColumn = 'name' | 'status' | 'template' | 'updated';

export function WebsitePage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const auth = useAuth();
  const { slug = '' } = useParams<{ slug: string }>();
  const confirm = useConfirm();
  const token = auth.user?.access_token ?? null;

  const [loading, setLoading] = useState(true);
  const [websites, setWebsites] = useState<Website[]>([]);
  const [importOpen, setImportOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setWebsites(await listWebsites(token, slug));
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // publicURLBase is the API origin's `/c` root — each row appends its
  // own slug so the dashboard's "open public site" action goes to the
  // right per-site URL even when an org has multiple live sites.
  const publicURLBase = useMemo(() => config.apiURL.replace(/\/api\/?$/, ''), []);

  const handlePublish = useCallback(
    async (id: string) => {
      await publishWebsite(token, slug, id);
      await reload();
    },
    [reload, slug, token],
  );

  const handleUnpublish = useCallback(
    async (id: string, name: string) => {
      if (!(await confirm({
        title: t('website.dashboard.unpublishConfirmTitle'),
        body: t('website.dashboard.unpublishConfirmBody', { name }),
        confirmLabel: t('website.dashboard.unpublish'),
        tone: 'destructive',
      }))) return;
      await unpublishWebsite(token, slug, id);
      await reload();
    },
    [confirm, reload, slug, t, token],
  );

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      if (!(await confirm({
        title: t('website.dashboard.deleteConfirmTitle'),
        body: t('website.dashboard.deleteConfirmBody', { name }),
        confirmLabel: t('website.dashboard.delete'),
        tone: 'destructive',
      }))) return;
      await deleteWebsite(token, slug, id);
      await reload();
    },
    [confirm, reload, slug, t, token],
  );

  // scheduleTarget is the row currently being scheduled — opening the
  // dialog vs. dismissing it just toggles this id. The dialog itself is
  // generic and rendered once at the page root.
  const [scheduleTarget, setScheduleTarget] = useState<{ id: string; name: string } | null>(null);

  const handleCancelSchedule = useCallback(
    async (id: string) => {
      await cancelScheduledPublishWebsite(token, slug, id);
      await reload();
    },
    [reload, slug, token],
  );

  const handleDiscard = useCallback(
    async (id: string, name: string) => {
      if (!(await confirm({
        title: t('website.dashboard.discardConfirmTitle'),
        body: t('website.dashboard.discardConfirmBody', { name }),
        confirmLabel: t('website.dashboard.discardCta'),
        tone: 'destructive',
      }))) return;
      await discardWebsitePending(token, slug, id);
      await reload();
    },
    [confirm, reload, slug, t, token],
  );

  const handleDuplicate = useCallback(
    async (id: string, name: string) => {
      // Default to "Copy of <name>" — matches the convention every
      // other "duplicate" feature in the SPA. User can rename via the
      // wizard immediately after.
      const newName = (t('website.dashboard.duplicateLabel', {
        name,
        defaultValue: `Copy of ${name}`,
      }) as string).slice(0, 80);
      try {
        const dup = await duplicateWebsite(token, slug, id, newName);
        navigate(`/orgs/${slug}/website/${dup.id}`);
      } catch (e) {
        // Reload anyway in case the dup landed despite the error
        // (e.g. RLS hiccup on the redirect path).
        await reload();
        throw e;
      }
    },
    [navigate, reload, slug, t, token],
  );

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('website.dashboard.eyebrow', { defaultValue: 'Websites' })}
          </p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('website.dashboard.title')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Import is a one-off action used to onboard from an
              existing site; on mobile it folds into a kebab so the
              header doesn't wrap to two lines. */}
          <Button
            type="button"
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="hidden sm:inline-flex"
          >
            <DownloadSimple className="h-4 w-4" weight="bold" />
            {t('websiteImport.openCta', { defaultValue: 'Import from existing site' })}
          </Button>
          <Button type="button" onClick={() => navigate(`/orgs/${slug}/website/new`)}>
            <Plus className="h-4 w-4" weight="bold" />
            {t('website.dashboard.addNew', { defaultValue: 'New website' })}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={t('rowActions.openMenu')}
              className="flex h-9 w-9 items-center justify-center rounded-pill border border-input bg-background text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
            >
              <DotsThree weight="bold" className="h-5 w-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setImportOpen(true)}>
                <DownloadSimple className="h-4 w-4" weight="bold" />
                {t('websiteImport.openCta', { defaultValue: 'Import from existing site' })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <WebsiteImportWizard
        slug={slug}
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />

      {loading ? (
        <TableSkeleton columns={6} />
      ) : websites.length === 0 ? (
        <EmptyState
          onAdd={() => navigate(`/orgs/${slug}/website/new`)}
        />
      ) : (
        <>
          <WebsiteList
            websites={websites}
            publicURLBase={publicURLBase}
            onEdit={(id) => navigate(`/orgs/${slug}/website/${id}/design`)}
            onDuplicate={handleDuplicate}
            onSchedule={(id, name) => setScheduleTarget({ id, name })}
            onCancelSchedule={handleCancelSchedule}
            onPublish={handlePublish}
            onUnpublish={handleUnpublish}
            onDiscard={handleDiscard}
            onDelete={handleDelete}
          />
          <ScheduleDateTimeDialog
            open={scheduleTarget !== null}
            title={t('website.dashboard.scheduleTitle', { name: scheduleTarget?.name ?? '' })}
            body={t('website.dashboard.scheduleBody')}
            confirmLabel={t('website.dashboard.scheduleCta')}
            onConfirm={async (whenISO) => {
              if (!scheduleTarget) return;
              await schedulePublishWebsite(token, slug, scheduleTarget.id, whenISO);
              await reload();
            }}
            onClose={() => setScheduleTarget(null)}
          />
        </>
      )}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card p-10 text-center">
      <Globe className="h-10 w-10 text-foreground/60" weight="duotone" />
      <h2 className="font-heading text-xl font-bold">
        {t('website.dashboard.emptyTitle', { defaultValue: 'No websites yet' })}
      </h2>
      <p className="max-w-md text-sm text-muted-foreground">
        {t('website.dashboard.emptyBody', {
          defaultValue:
            "Click 'New website' above to set up your first public site through the 4-step wizard.",
        })}
      </p>
      <Button type="button" onClick={onAdd}>
        <Plus className="h-4 w-4" weight="bold" />
        {t('website.dashboard.addNew', { defaultValue: 'New website' })}
      </Button>
    </div>
  );
}

function WebsiteList({
  websites,
  publicURLBase,
  onEdit,
  onDuplicate,
  onSchedule,
  onCancelSchedule,
  onPublish,
  onUnpublish,
  onDiscard,
  onDelete,
}: {
  websites: Website[];
  publicURLBase: string;
  onEdit: (id: string) => void;
  onDuplicate: (id: string, name: string) => void;
  onSchedule: (id: string, name: string) => void;
  onCancelSchedule: (id: string) => void;
  onPublish: (id: string) => void;
  onUnpublish: (id: string, name: string) => void;
  onDiscard: (id: string, name: string) => void;
  onDelete: (id: string, name: string) => void;
}) {
  const { t } = useTranslation('common');
  const scrollRef = useRef<HTMLDivElement>(null);
  const elevated = useHorizontalScrollEdge(scrollRef);

  // Six tracks: name | status | changes | template | updated | actions.
  // "Changes" is its own column so the status pill stays a single clean
  // word ("Publicerad" / "Utkast") instead of wrapping awkwardly with a
  // "· opublicerade ändringar" suffix.
  //
  // Each data track is a minmax(px, fr) so the table keeps a sensible
  // minimum width and overflows into a horizontal scroll on narrow
  // screens — same behaviour as the Teams and Members tables — instead of
  // crushing the columns (and truncating the name) to fit the viewport.
  // The trailing 4rem actions column matches ACTIONS_COL in Teams/Members
  // so the sticky 3-dot rail lines up across pages.
  const template =
    'minmax(200px, 2.5fr) minmax(110px, 0.9fr) minmax(120px, 1.1fr) minmax(120px, 1fr) minmax(130px, 1fr) 4rem';

  const sortState = useTableSortState<SortColumn>({
    columns: ['name', 'status', 'template', 'updated'] as const,
  });

  const sortedWebsites = useMemo(() => {
    if (!sortState.sort) return websites;
    const factor = sortState.dir === 'asc' ? 1 : -1;
    return [...websites].sort((a, b) => {
      switch (sortState.sort) {
        case 'name':
          return a.name.localeCompare(b.name) * factor;
        case 'status':
          return a.status.localeCompare(b.status) * factor;
        case 'template':
          return a.template.localeCompare(b.template) * factor;
        case 'updated':
          return (
            (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()) *
            factor
          );
        default:
          return 0;
      }
    });
  }, [websites, sortState.sort, sortState.dir]);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-border bg-background shadow-sm"
      style={{ clipPath: 'inset(0 round var(--radius-2xl))' }}
    >
      <div ref={scrollRef} className="overflow-x-auto">
        <div className="min-w-fit divide-y divide-border">
          <div
            className="hidden bg-muted/40 py-3 sm:grid"
            style={{ gridTemplateColumns: template }}
            role="row"
          >
            <span className="px-3" role="columnheader">
              <SortableHeaderButton
                column="name"
                label={t('website.dashboard.colName', { defaultValue: 'Name' })}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="status"
                label={t('website.dashboard.statusLabel')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2 font-heading text-[0.75rem] font-semibold uppercase tracking-wider text-muted-foreground" role="columnheader">
              {t('website.dashboard.changesLabel')}
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="template"
                label={t('website.dashboard.templateLabel')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="updated"
                label={t('website.dashboard.colUpdated', { defaultValue: 'Last updated' })}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <StickyEndCell elevated={false} bgClass="bg-muted/40" className="justify-end">
              <span className="sr-only">{t('rowActions.columnHeader')}</span>
            </StickyEndCell>
          </div>

          {sortedWebsites.map((w) => (
            <WebsiteRow
              key={w.id}
              website={w}
              template={template}
              elevated={elevated}
              publicHref={`${publicURLBase}/c/${encodeURIComponent(w.slug)}`}
              onEdit={() => onEdit(w.id)}
              onDuplicate={() => onDuplicate(w.id, w.name)}
              onSchedule={() => onSchedule(w.id, w.name)}
              onCancelSchedule={() => onCancelSchedule(w.id)}
              onPublish={() => onPublish(w.id)}
              onUnpublish={() => onUnpublish(w.id, w.name)}
              onDiscard={() => onDiscard(w.id, w.name)}
              onDelete={() => onDelete(w.id, w.name)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function WebsiteRow({
  website,
  template,
  elevated,
  publicHref,
  onEdit,
  onDuplicate,
  onSchedule,
  onCancelSchedule,
  onPublish,
  onUnpublish,
  onDiscard,
  onDelete,
}: {
  website: Website;
  template: string;
  elevated: boolean;
  publicHref: string;
  onEdit: () => void;
  onDuplicate: () => void;
  onSchedule: () => void;
  onCancelSchedule: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDiscard: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('common');
  const isPublished = website.status === 'published';
  const hasPending = website.has_pending_changes;
  const updated = new Date(website.updated_at).toLocaleDateString();

  const fastActions: RowAction[] = [
    {
      id: 'edit',
      label: t('website.dashboard.edit'),
      icon: PencilSimple,
      onSelect: onEdit,
    },
    isPublished
      ? {
          id: 'unpublish',
          label: t('website.dashboard.unpublish'),
          icon: EyeSlash,
          onSelect: onUnpublish,
        }
      : {
          id: 'publish',
          label: t('website.dashboard.publish'),
          icon: Eye,
          onSelect: onPublish,
        },
  ];

  const menuActions: RowAction[] = [
    {
      id: 'edit',
      label: t('website.dashboard.edit'),
      icon: PencilSimple,
      onSelect: onEdit,
    },
    {
      id: 'duplicate',
      label: t('website.dashboard.duplicate'),
      icon: Copy,
      onSelect: onDuplicate,
    },
    ...(hasPending
      ? ([{
          id: 'discard',
          label: t('website.dashboard.discard'),
          icon: ArrowCounterClockwise,
          onSelect: onDiscard,
          destructive: true,
        }] satisfies RowAction[])
      : []),
    ...(isPublished
      ? []
      : ([{
          id: 'schedule',
          label: website.scheduled_publish_at
            ? t('website.dashboard.scheduleReschedule')
            : t('website.dashboard.scheduleAction'),
          icon: Calendar,
          onSelect: onSchedule,
        }] satisfies RowAction[])),
    ...(website.scheduled_publish_at
      ? ([{
          id: 'cancel-schedule',
          label: t('website.dashboard.scheduleCancel'),
          icon: XCircle,
          onSelect: onCancelSchedule,
        }] satisfies RowAction[])
      : []),
    isPublished
      ? {
          id: 'unpublish',
          label: t('website.dashboard.unpublish'),
          icon: EyeSlash,
          onSelect: onUnpublish,
        }
      : {
          id: 'publish',
          label: t('website.dashboard.publish'),
          icon: Eye,
          onSelect: onPublish,
        },
    ...(isPublished
      ? [
          {
            id: 'visit',
            label: t('website.dashboard.visit', { defaultValue: 'Open public site' }),
            icon: ArrowSquareOut,
            onSelect: () => window.open(publicHref, '_blank', 'noopener'),
          } satisfies RowAction,
        ]
      : []),
    {
      id: 'delete',
      label: t('website.dashboard.delete'),
      icon: Trash,
      onSelect: onDelete,
      destructive: true,
    },
  ];

  return (
    <div
      className="group grid items-stretch transition-colors hover:bg-muted/40"
      style={{ gridTemplateColumns: template }}
      role="row"
    >
      <span className="flex min-w-0 flex-col justify-center gap-0.5 px-3 py-3" role="cell">
        <button
          type="button"
          className="text-left font-heading text-sm font-semibold text-foreground hover:underline"
          onClick={onEdit}
        >
          {website.name}
        </button>
        {isPublished && (
          <a
            className="truncate text-xs text-muted-foreground hover:text-foreground"
            href={publicHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {publicHref.replace(/^https?:\/\//, '')}
          </a>
        )}
        {!isPublished && website.scheduled_publish_at && (
          <span className="truncate text-xs text-muted-foreground">
            {t('website.dashboard.scheduledFor', {
              when: new Date(website.scheduled_publish_at).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
                hour12: false,
              }),
            })}
          </span>
        )}
      </span>

      <span className="flex items-center px-2 py-3" role="cell">
        <StatusBadge status={website.status} />
      </span>

      <span className="flex items-center px-2 py-3" role="cell">
        {hasPending && <ChangesBadge />}
      </span>

      <span className="flex items-center px-2 py-3 text-sm text-foreground/80 capitalize" role="cell">
        {t(`website.wizard.template.${website.template}.name`)}
      </span>

      <span className="flex items-center px-2 py-3 text-sm tabular-nums text-foreground/80" role="cell">
        {updated}
      </span>

      <StickyEndCell
        elevated={elevated}
        bgClass="bg-background group-hover:bg-muted"
      >
        <RowActions fastActions={fastActions} menuActions={menuActions} />
      </StickyEndCell>
    </div>
  );
}

function StatusBadge({ status }: { status: Website['status'] }) {
  const { t } = useTranslation('common');
  const isPublished = status === 'published';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5 text-xs font-semibold',
        isPublished
          ? 'bg-[var(--lime-light,#D4F56A)] text-foreground'
          : 'bg-muted text-foreground/70',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          isPublished ? 'bg-[var(--lime,#BEE034)]' : 'bg-foreground/40',
        )}
      />
      {t(isPublished ? 'website.dashboard.status.published' : 'website.dashboard.status.draft')}
    </span>
  );
}

function ChangesBadge() {
  const { t } = useTranslation('common');
  return (
    <span className="inline-flex items-center gap-1.5 rounded-pill border border-amber-300/60 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
      {t('website.dashboard.changes.pending')}
    </span>
  );
}
